import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowLeft,
  Phone,
  Video,
  MoreVertical,
  Send,
  Mic,
  Paperclip,
  Smile,
  Palette
} from 'lucide-react';
import { useChat, useMessages } from '@/hooks/mensag/useChats';
import { chatThemes, type ChatTheme, getTheme } from '@/components/mensag/ChatThemes';
import { MessageBubble } from '@/components/mensag/MessageBubble';

export default function ChatPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const [messageInput, setMessageInput] = useState('');
  const [currentTheme, setCurrentTheme] = useState<ChatTheme>('default');
  const navigate = useNavigate();

  const { chat, isLoading: chatLoading } = useChat(contactId || '');
  const { data: messages, isLoading: messagesLoading } = useMessages(contactId || '');

  if (chatLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-gray-500 dark:text-gray-400 mb-4">Chat no encontrado</p>
        <Button onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    // TODO: Send message via Nostr
    console.log('Sending message:', messageInput);
    setMessageInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const theme = getTheme(currentTheme);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="md:hidden p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <div className="relative">
            <Avatar className="h-10 w-10">
              <AvatarImage src={chat.contactAvatar} />
              <AvatarFallback>{chat.contactName.charAt(0)}</AvatarFallback>
            </Avatar>
            {chat.isOnline && (
              <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-white rounded-full"></div>
            )}
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {chat.contactName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {chat.isOnline ? 'En línea' : 'Desconectado'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const themes = Object.keys(chatThemes) as ChatTheme[];
              const currentIndex = themes.indexOf(currentTheme);
              const nextIndex = (currentIndex + 1) % themes.length;
              setCurrentTheme(themes[nextIndex]);
            }}
            title={`Tema: ${getTheme(currentTheme).name}`}
          >
            <Palette className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="sm">
            <Phone className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="sm">
            <Video className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className={`flex-1 p-4 ${theme.background}`}>
        <div className="space-y-4 max-w-4xl mx-auto">
          {messagesLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <div className="max-w-xs px-4 py-2 bg-gray-200 rounded-lg">
                    <div className="w-24 h-4 bg-gray-300 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : (!messages || !Array.isArray(messages) || messages.length === 0) ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">👋</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                ¡Comienza la conversación!
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Envía el primer mensaje para iniciar el chat
              </p>
            </div>
          ) : (
            (messages && Array.isArray(messages) ? messages : []).map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isGroup={false} // For now, assume 1-on-1 chat
                showTimestamp={
                  index === 0 ||
                  (index > 0 && messages[index - 1] &&
                   Math.abs(message.timestamp.getTime() - messages[index - 1].timestamp.getTime()) > 300000) // 5 minutes
                }
                theme={{
                  bubbleSent: theme.bubbleSent,
                  bubbleReceived: theme.bubbleReceived
                }}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center space-x-2 max-w-4xl mx-auto">
          <Button variant="ghost" size="sm">
            <Paperclip className="h-5 w-5" />
          </Button>

          <div className="flex-1 relative">
            <Input
              placeholder="Escribe un mensaje..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pr-12"
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2"
            >
              <Smile className="h-4 w-4" />
            </Button>
          </div>

          {messageInput.trim() ? (
            <Button
              onClick={handleSendMessage}
              size="sm"
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Send className="h-4 w-4" />
            </Button>
          ) : (
            <Button variant="ghost" size="sm">
              <Mic className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}